/**
 * Consigli sugli annunci (OTTO.md §4.5 — "il mezzo").
 *
 * «Non esegue. Propone.» Ogni consiglio è un bottone: *Applica* o *Ignora*.
 * Quando c'è un testo pronto, "Applica" crea un'azione normale — stessa
 * anteprima, stessi guardrail, stessa ricevuta di qualsiasi altra modifica.
 * Quando il testo non c'è, il consiglio passa la palla alla chat, perché
 * scrivere una frase che finirà su Airbnb richiede il modello.
 *
 * È la schermata che in demo fa dire "wow", perché mostra qualcosa che il
 * property manager oggi **non vede**.
 */

import { useEffect, useState } from "react";
import { Building2, Check, MessageSquare, Sparkles, X } from "lucide-react";
import type { SuggestionView } from "@otto/shared";
import { api } from "../lib/api.ts";
import { messaggioDi } from "../lib/errori.ts";
import { useStore } from "../lib/store.tsx";
import { Badge, Card, EmptyState, Pill, Skeleton } from "../ui/primitives.tsx";

const CATEGORIE: Record<
  string,
  { label: string; tone: "plain" | "warn" | "accent" }
> = {
  titolo: { label: "Titolo", tone: "accent" },
  descrizione: { label: "Descrizione", tone: "plain" },
  informazione_mancante: { label: "Manca un dato", tone: "warn" },
  come_arrivare: { label: "Come arrivare", tone: "plain" },
  regole: { label: "Regole", tone: "plain" },
  foto: { label: "Foto", tone: "plain" },
  prezzo: { label: "Prezzi", tone: "accent" },
};

export function Suggestions({
  onAskOtto,
  onOpenListing,
}: {
  onAskOtto: (prompt: string) => void;
  onOpenListing: (listingId: string) => void;
}) {
  const store = useStore();
  const [suggestions, setSuggestions] = useState<SuggestionView[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = () =>
    api
      .suggestions()
      .then(({ suggestions: loaded }) => setSuggestions(loaded))
      .catch(() => setSuggestions([]));

  useEffect(() => {
    void load();
  }, []);

  const apply = async (suggestion: SuggestionView) => {
    setBusy(suggestion.id);
    try {
      await api.applySuggestion(suggestion.id);
      await store.refreshActions();
      store.notify(
        "Ho preparato la modifica: la trovi fra le proposte da confermare.",
      );
      await load();
    } catch (error) {
      store.notify(messaggioDi(error));
    } finally {
      setBusy(null);
    }
  };

  const ignore = async (suggestion: SuggestionView) => {
    setBusy(suggestion.id);
    try {
      await api.ignoreSuggestion(suggestion.id);
      await load();
    } catch (errore) {
      store.notify(messaggioDi(errore));
    } finally {
      setBusy(null);
    }
  };

  if (!suggestions) {
    return (
      <div className="grid gap-3 lg:grid-cols-2">
        {Array.from({ length: 6 }, (_, index) => (
          <Skeleton key={index} className="h-[150px]" />
        ))}
      </div>
    );
  }

  if (suggestions.length === 0) {
    return (
      <EmptyState
        icon={<Sparkles size={28} />}
        title="Non ho trovato niente da sistemare"
        hint="Controllo gli annunci a ogni sincronizzazione: se compare qualcosa da migliorare, lo trovi qui."
      />
    );
  }

  // Raggruppati per immobile: si sistema un annuncio per volta, non un
  // difetto per volta sparso su quindici case.
  const byListing = new Map<string, SuggestionView[]>();
  for (const suggestion of suggestions) {
    byListing.set(suggestion.listingNickname, [
      ...(byListing.get(suggestion.listingNickname) ?? []),
      suggestion,
    ]);
  }

  return (
    <div className="flex flex-col gap-5">
      <p className="text-[13px] leading-relaxed text-ink-soft">
        Ho guardato i tuoi annunci e questi sono i punti deboli. Non cambio
        niente da solo: decidi tu, uno per uno.
      </p>

      {[...byListing.entries()].map(([nickname, group]) => (
        <section key={nickname} className="flex flex-col gap-2.5">
          <h2 className="px-1 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">
            {nickname} · {group.length}
          </h2>

          <div className="grid gap-2.5 lg:grid-cols-2">
            {group.map((suggestion) => {
              const categoria = CATEGORIE[suggestion.categoria] ?? {
                label: suggestion.categoria,
                tone: "plain" as const,
              };

              return (
                <Card key={suggestion.id} className="flex flex-col gap-2.5 p-4">
                  <div className="flex items-start gap-2">
                    <p className="flex-1 text-[15px] font-bold leading-snug text-ink">
                      {suggestion.titolo}
                    </p>
                    <Badge tone={categoria.tone}>{categoria.label}</Badge>
                  </div>

                  <p className="text-[13px] leading-relaxed text-ink-soft">
                    {suggestion.testo}
                  </p>

                  <div className="mt-auto flex flex-wrap gap-2 pt-0.5">
                    {suggestion.hasAction ? (
                      <Pill
                        variant="primary"
                        size="sm"
                        icon={<Check size={14} />}
                        loading={busy === suggestion.id}
                        onClick={() => apply(suggestion)}
                      >
                        Applica
                      </Pill>
                    ) : (
                      // Anche senza un testo già pronto il consiglio deve poter
                      // portare da qualche parte: la palla passa alla chat, che
                      // è l'unica che sa scrivere.
                      <Pill
                        variant="primary"
                        size="sm"
                        icon={<MessageSquare size={14} />}
                        onClick={() =>
                          onAskOtto(
                            suggestion.askOtto ?? comandoDiRipiego(suggestion),
                          )
                        }
                      >
                        Fallo scrivere a Otto
                      </Pill>
                    )}

                    <Pill
                      variant="secondary"
                      size="sm"
                      icon={<Building2 size={14} />}
                      onClick={() => onOpenListing(suggestion.listingId)}
                    >
                      Vedi l'immobile
                    </Pill>

                    <Pill
                      variant="ghost"
                      size="sm"
                      icon={<X size={14} />}
                      loading={busy === suggestion.id}
                      onClick={() => ignore(suggestion)}
                    >
                      Ignora
                    </Pill>
                  </div>
                </Card>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

/**
 * Il comando da mandare in chat quando il consiglio non ne porta uno suo.
 *
 * Meglio una frase costruita dal titolo che un consiglio senza uscite: il
 * property manager ha letto un problema, deve poterlo girare a Otto.
 */
function comandoDiRipiego(suggestion: SuggestionView): string {
  return `Sistema questo su "${suggestion.listingNickname}": ${suggestion.titolo}`;
}
